import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { finalize, tap } from 'rxjs/operators';
import { BaseComponent } from '../../base-components/base.component';
import { DocumentSearchCriteria } from '../../models/document/document-search-criteria.model';
import { DocumentService } from '../../services/document/document.service';
import { FileSaverService } from '../../services/output/file-saver.service';
import { DocumentItemKeyType } from '../../../Foundation/models/enums/document-item-key-type.enum';
import { ValidationModel } from '../../models/validation-results/validation.model';
import { DocumentSearchResult } from '@max/shared/models/document/document-search-result.model';
import { DocumentCategoryRequirement } from '@max/shared/models/document/document-category-requirement.model';

export class DocumentSearchContext {
    constructor(
        public documentItemKeyTypeId?: number,
        public itemKeyId?: number,
        public hideUserInput?: boolean,
        public disableSelect?: boolean,
        public startDate: Date | null = null,
        public endDate: Date | null = null
    ) {}
}

@Component({
    selector: 'document-search-modal',
    templateUrl: './document-search-modal.html'
})
export class DocumentSearchModalComponent extends BaseComponent {
    private searchDocumentData: DocumentSearchCriteria;
    public form: UntypedFormGroup;
    public documents: DocumentCategoryRequirement[] = [];
    public documentLookups: any;
    private documentTypes: any[] = new Array();
    public loadSearch = false;
    public showResults = false;
    private documentCollection: any[] = [];
    public rowsPerPage = 10;

    constructor(public dialogRef: MatDialogRef<DocumentSearchModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: DocumentSearchContext,
        private documentService: DocumentService,
        private formBuilder: UntypedFormBuilder,
        private fileSaverService: FileSaverService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.documentService.getDocumentLookups()
            .subscribe(res => {
                this.documentLookups = res.result;
                for (var i = 0; i < this.documentLookups.documentTypes.length; i++) {
                    this.documentTypes.push({ label: this.documentLookups.documentTypes[i].text, value: this.documentLookups.documentTypes[i].value });
                }
                this.loadSearch = true;
            });

        this.form = this.formBuilder.group({
            documentItemKeyTypeControl: [this.data.documentItemKeyTypeId || ''],
            itemKeyControl: [this.data.itemKeyId || ''],
            documentCategoryTypeControl: [''],
            documentTypeControl: ['']
        });

        if ((null != this.data.documentItemKeyTypeId) && (null != this.data.itemKeyId)) {
            this.retrieve();
        }
    }

    public retrieve = (): void => {
        this.searchDocumentData = new DocumentSearchCriteria();
        this.searchDocumentData.documentItemKeyTypeId = this.form.value.documentItemKeyTypeControl;
        this.searchDocumentData.itemKeyId = this.form.value.itemKeyControl;
        this.searchDocumentData.documentCategoryTypeId = this.form.value.documentCategoryTypeControl;
        this.searchDocumentData.documentTypeId = this.form.value.documentTypeControl;
        this.searchDocumentData.startDate = this.data.startDate;
        this.searchDocumentData.endDate = this.data.endDate;

        this.documentService.retrieveDocumentMetadata(this.searchDocumentData)
            .subscribe(res => {
                const documentCategoryRequirements = this.buildDocumentCategoryRequirements(res.result);
                this.addToDisplayDocuments(documentCategoryRequirements);
                this.showResults = true;
            });
    };

    downloadDocument(document) {
        document.loading = true;

        this.documentService.getDocument(document.documentId).pipe(
            finalize(() => document.loading = false))
            .subscribe(
                res => {
                    const customerNumberId = res.documentItemKeys.find(key => key.documentItemKeyTypeId === new DocumentItemKeyType().Customer.id)?.itemKeyId;
                    if (customerNumberId) {
                        this.documentService.isDocumentsViewRestricted(customerNumberId).subscribe(
                            isRestricted => {
                                if (isRestricted.result === true) {
                                    this.handleValidationResult(new ValidationModel([{ state: { validationType: 'Error' }, errorMessage: 'Customer document is restricted' }]));
                                } else {
                                    this.download(res);
                                }
                            },
                            err => {
                                this.handleValidationResult(err);
                            },
                            () => { });
                    } else {
                        this.download(res);
                    }
                },
                err => {
                    this.handleValidationResult(err);
                },
                () => { }
            );
    }

    private download(document): void {
        var result = document;
        this.saveDocumentToFile(result);
    }

    private saveDocumentToFile = (document): void => {
        try {
            this.fileSaverService.saveBase64ToFile(document.documentFilename, document.documentContent, document.documentMimeTypeValue);
        } catch (e) {
            this.handleValidationResult(e);
        }
    };

    public selectDocument = (item: any, selected: boolean): void => {
        if (selected) {
            this.documentCollection.push(item);
        } else {
            var index = this.documentCollection.findIndex(o => o.documentId === item.documentId);

            if (index !== -1) {
            } this.documentCollection.splice(index, 1);
        }
    };

    public cancel = (): void => {
        this.dialogRef.close();
    };

    public choose = (): void => {
        this.dialogRef.close(this.documentCollection);
    };

    /**
     * Builds the data structure that is needed for the UI from the array of DocumentSearchResults.
     * This is a flattend out data structure of document, documentRequirment (documentType and documentCategory)
     */
    private buildDocumentCategoryRequirements(documents: DocumentSearchResult[]): DocumentCategoryRequirement[] {
        const documentCategoryRequirements = [];
        if (documents != null && documents.length > 0) {
            for (const document of documents) {
                const documentCategoryRequirement = new DocumentCategoryRequirement();
                documentCategoryRequirement.documentId = document.documentId;
                documentCategoryRequirement.documentType = document.documentTypeDisplayValue;
                documentCategoryRequirement.documentFilename = document.documentFilename;
                documentCategoryRequirement.updateDateDisplayValue = document.updatedDate;

                if (document.documentRequirements != null && document.documentRequirements.length > 0) {
                    for (const requirement of document.documentRequirements) {
                        documentCategoryRequirement.documentCategoryType = requirement.documentCategoryType;
                        documentCategoryRequirement.documentDescription = document.documentDescription;
                        documentCategoryRequirements.push(documentCategoryRequirement);
                    }
                } else {
                    documentCategoryRequirements.push(documentCategoryRequirement);
                }
            }
        }
        return documentCategoryRequirements;
    }

    private addToDisplayDocuments(documentCategoryRequirements: DocumentCategoryRequirement[]) {
        documentCategoryRequirements.forEach(item => this.documents.push(item));
    }
}
